import React, { useEffect } from "react";
import isEmpty from "lodash/isEmpty";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import InteractiveGridWithInputs from "components/Input/InteractiveGridWithInputs";
import EditableLabelField from "components/Input/EditableLabelField";
import IncrementCounter from "components/Input/IncrementCounter";

import { getValidation, requiredNoZeroes } from "utils/fieldValidation";
import { getBaseUnits, getUnitOptions } from "utils/fields";
import { getPresetTOUOptions, getPresetTOUTemplate, containsZero } from "utils/transportation";
import { useStateSelector } from "utils/field";

const numberOfPeriodsValidation = getValidation("numberOfPeriods");
const periodPriceValidation = getValidation("timeOfUsePrice");

const createEmptySchedule = () => {
    const schedule = {};
    for (let month = 0; month < 12; month += 1) {
        schedule[month] = {};
        for (let hour = 0; hour < 24; hour += 1) {
            schedule[month][hour] = 0;
        }
    }
    return schedule;
};

const cleanSchedule = (schedule, numOfPeriods) => {
    const cleaned = {};
    for (let month = 0; month < 12; month += 1) {
        cleaned[month] = {};
        const row = schedule?.[month] || {};
        for (let hour = 0; hour < 24; hour += 1) {
            const value = Number(row?.[hour]) || 0;
            cleaned[month][hour] = value > numOfPeriods ? 0 : value;
        }
    }
    return cleaned;
};

export default React.memo(({ accessor, change }) => {
    const timeSlots = [
        "12am",
        "1am",
        "2am",
        "3am",
        "4am",
        "5am",
        "6am",
        "7am",
        "8am",
        "9am",
        "10am",
        "11am",
        "12pm",
        "1pm",
        "2pm",
        "3pm",
        "4pm",
        "5pm",
        "6pm",
        "7pm",
        "8pm",
        "9pm",
        "10pm",
        "11pm",
    ];
    const months = ["Jan.", "Feb.", "Mar.", "Apr.", "May.", "Jun.", "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."];

    const timeOfUsePriceUnits = useStateSelector(`${accessor}.timeOfUsePrice_u`);
    const weekdayScheduleTOU = useStateSelector(`${accessor}.weekdayScheduleTOU`) || createEmptySchedule();
    const weekendScheduleTOU = useStateSelector(`${accessor}.weekendScheduleTOU`) || createEmptySchedule();
    const numberOfPeriods = useStateSelector(`${accessor}.numberOfPeriods`) || 1;
    const timeOfUseLabels =
        useStateSelector(`${accessor}.timeOfUseLabels`) ||
        Array.from({ length: numberOfPeriods }, (_, index) => `Rate ${index + 1}`);
    const periodPrices =
        useStateSelector(`${accessor}.periodPrices`) || Array.from({ length: numberOfPeriods }).fill(0);
    const presetTOU = useStateSelector(`${accessor}.presetTOU`) || "User Defined";
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const presetTOUMap = getPresetTOUTemplate();
    const presetTimeOfUseOpts = getPresetTOUOptions(presetTOUMap);

    useEffect(() => {}, [timeOfUseLabels]);

    const handlePresetTOUChange = (selectedPreset) => {
        change(`${accessor}.presetTOU`, selectedPreset); // update the presetTOU state
        const presetData = presetTOUMap?.[selectedPreset] || {};

        if (isEmpty(presetData)) {
            // no presets loaded
            console.error("Preset data is not found");
        } else if (selectedPreset === "User Defined") {
            // user-defined case
            change(`${accessor}.numberOfPeriods`, 1);
            change(`${accessor}.periodPrices`, Array.from({ length: numberOfPeriods }).fill(0));
            change(`${accessor}.weekdayScheduleTOU`, createEmptySchedule());
            change(`${accessor}.weekendScheduleTOU`, createEmptySchedule());
            change(
                `${accessor}.timeOfUseLabels`,
                Array.from({ length: numberOfPeriods }, (_, i) => `Rate ${i + 1}`)
            );
        } else {
            // preset case
            change(`${accessor}.numberOfPeriods`, presetData.numberOfPeriods);
            change(`${accessor}.weekdayScheduleTOU`, presetData.Weekday);
            change(`${accessor}.weekendScheduleTOU`, presetData.Weekend);
            change(`${accessor}.timeOfUseLabels`, presetData.labels);
            // set the period prices based on the preset data
            for (let i = 0; i < presetData.numberOfPeriods; i++) {
                const price = presetData.periodPrices[`period${i + 1}`];
                change(`${accessor}.periodPrices[${i}]`, price);
            }
        }
    };

    const handleNumberOfPeriodsChange = (numOfPeriods) => {
        change(`${accessor}.numberOfPeriods`, numOfPeriods);

        change(`${accessor}.weekdayScheduleTOU`, cleanSchedule(weekdayScheduleTOU, numOfPeriods));
        change(`${accessor}.weekendScheduleTOU`, cleanSchedule(weekendScheduleTOU, numOfPeriods));
    };

    const handleLabelSave = (index, newLabel) => {
        const updatedLabels = [...timeOfUseLabels]; // create a new array
        updatedLabels[index] = newLabel; // update the label at the specific index
        change(`${accessor}.timeOfUseLabels`, updatedLabels); // update the state with the new array
    };

    return (
        <>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.presetTOU`}
                    options={presetTimeOfUseOpts}
                    label="Pre-set Schedule"
                    placeholder={presetTOU}
                    onChange={handlePresetTOUChange}
                    setValue={presetTOU}
                />
                <Field
                    component={IncrementCounter}
                    name={`${accessor}.numberOfPeriods`}
                    label="Number of Rates"
                    change={change}
                    min={1}
                    max={9}
                    validate={numberOfPeriodsValidation}
                    setValue={numberOfPeriods}
                    onChange={(value) => handleNumberOfPeriodsChange(value)}
                    disabled={presetTOU !== "User Defined"}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr">
                {numberOfPeriods >= 1 &&
                    numberOfPeriods <= 9 &&
                    Array.from({ length: numberOfPeriods }).map((_, index) => {
                        const periodNumber = index;
                        return (
                            <Field
                                key={periodNumber}
                                component={InputWithUnits}
                                type="number"
                                name={`${accessor}.periodPrices[${periodNumber}]`}
                                validate={periodPriceValidation}
                                label={
                                    <EditableLabelField
                                        index={index}
                                        label={timeOfUseLabels[periodNumber] || `Rate ${periodNumber + 1}`}
                                        onSave={(newLabel) => handleLabelSave(periodNumber, newLabel)}
                                    />
                                }
                                decimals={3}
                                change={change}
                                units={{
                                    base: getBaseUnits("timeOfUsePrice", modelUnits),
                                    options: getUnitOptions("timeOfUsePrice"),
                                    selected: timeOfUsePriceUnits,
                                    unitType: "timeOfUsePrice",
                                    accessor: `${accessor}.timeOfUsePrice_u`,
                                }}
                                placeholder={"0.000"}
                                setValue={periodPrices[periodNumber] || 0}
                                disabled={presetTOU !== "User Defined"}
                            />
                        );
                    })}
            </InputRow>
            <InputRow gridTemplate="1fr">
                {presetTOU !== "User Defined" ? (
                    <>
                        <Field // preset case
                            component={InteractiveGridWithInputs}
                            key={`weekday-${presetTOU}`}
                            name={`${accessor}.weekdayScheduleTOU`}
                            label="Weekday TOU Schedule"
                            info="Time-of-Use (TOU) Schedule. To make a selection: click and drag, or make single selections. To deselect: press 'esc' after the current selection has been made."
                            customValidateFunc={containsZero(weekdayScheduleTOU) ? requiredNoZeroes(true) : null}
                            inputArray={weekdayScheduleTOU}
                            columnTitles={timeSlots}
                            rowTitles={months}
                            numberOfPeriods={numberOfPeriods}
                            setValue={weekdayScheduleTOU}
                            warning={containsZero(weekdayScheduleTOU) ? "emptyTimeOfUseSchedule" : ""}
                            disabledButton={true}
                            disabled={numberOfPeriods < 1 || numberOfPeriods > 9}
                        />
                        <Field
                            component={InteractiveGridWithInputs}
                            key={`weekend-${presetTOU}`}
                            name={`${accessor}.weekendScheduleTOU`}
                            label="Weekend TOU Schedule"
                            info="Time-of-Use (TOU) Schedule. To make a selection: click and drag, or make single selections. To deselect: press 'esc' after the current selection has been made."
                            customValidateFunc={containsZero(weekendScheduleTOU) ? requiredNoZeroes : undefined}
                            inputArray={weekendScheduleTOU}
                            columnTitles={timeSlots}
                            rowTitles={months}
                            numberOfPeriods={numberOfPeriods}
                            setValue={weekendScheduleTOU}
                            warning={containsZero(weekendScheduleTOU) ? "emptyTimeOfUseSchedule" : ""}
                            disabledButton={true}
                            disabled={numberOfPeriods < 1 || numberOfPeriods > 9}
                        />
                    </>
                ) : (
                    // user-defined case
                    <>
                        <Field
                            component={InteractiveGridWithInputs}
                            key={`weekday-userdefined`}
                            name={`${accessor}.weekdayScheduleTOU`}
                            label="Weekday TOU Schedule"
                            info="Time-of-Use (TOU) Schedule. To make a selection: click and drag, or make single selections. To deselect: press 'esc' after the current selection has been made."
                            customValidateFunc={containsZero(weekdayScheduleTOU) ? requiredNoZeroes : undefined}
                            columnTitles={timeSlots}
                            rowTitles={months}
                            inputArray={weekdayScheduleTOU}
                            numberOfPeriods={numberOfPeriods}
                            warning={containsZero(weekdayScheduleTOU) ? "emptyTimeOfUseSchedule" : ""}
                            setValue={weekdayScheduleTOU}
                            disabled={numberOfPeriods < 1 || numberOfPeriods > 9}
                        />
                        <Field
                            component={InteractiveGridWithInputs}
                            key={`weekend-userdefined`}
                            name={`${accessor}.weekendScheduleTOU`}
                            label="Weekend TOU Schedule"
                            info="Time-of-Use (TOU) Schedule. To make a selection: click and drag, or make single selections. To deselect: press 'esc' after the current selection has been made."
                            customValidateFunc={containsZero(weekendScheduleTOU) ? requiredNoZeroes : undefined}
                            columnTitles={timeSlots}
                            rowTitles={months}
                            inputArray={weekendScheduleTOU}
                            numberOfPeriods={numberOfPeriods}
                            warning={containsZero(weekendScheduleTOU) ? "emptyTimeOfUseSchedule" : ""}
                            setValue={weekendScheduleTOU}
                            disabled={numberOfPeriods < 1 || numberOfPeriods > 9}
                        />
                    </>
                )}
            </InputRow>
        </>
    );
});
